import {
  BarChart3,
  Bookmark,
  Heart,
  MessageCircle,
  MoreHorizontal,
  Play,
  Repeat2,
  Send,
  Share2,
  Star,
  ThumbsUp,
} from 'lucide-react'
import { PLATFORMS, type PlatformId } from '@/lib/platforms'
import type { Channel, MediaItem } from '@/lib/types'
import { channelProfileSource, PlatformBadge, ProfileImage } from '@/components/ChannelAvatar'

function PlatformIcon({ platform, size = 18 }: { platform: PlatformId; size?: number }) {
  const item = PLATFORMS[platform]
  return (
    <span
      className="inline-flex shrink-0 items-center justify-center"
      style={{
        width: size,
        height: size,
        color: item.color === '#ffffff' ? '#111111' : item.color,
      }}
      aria-label={item.name}
    >
      {item.icon}
    </span>
  )
}

function MediaItemView({ item, className }: { item: MediaItem; className: string }) {
  if (!item.url) {
    return (
      <div className={`${className} grid place-items-center bg-ink-100 text-xs font-medium text-ink-500`}>
        Media unavailable
      </div>
    )
  }
  return item.type === 'video' ? (
    <video src={item.url} className={className} muted playsInline />
  ) : (
    <img src={item.url} className={className} alt="Post media preview" />
  )
}

function MediaGrid({ media, square = false }: { media: MediaItem[]; square?: boolean }) {
  if (media.length === 0) return null
  const shown = media.slice(0, 4)
  return (
    <div
      className={`mt-3 grid overflow-hidden rounded-xl border border-line bg-ink-100 ${
        shown.length === 1 ? 'grid-cols-1' : 'grid-cols-2'
      }`}
    >
      {shown.map((item) => (
        <MediaItemView
          key={item.id}
          item={item}
          className={`${square ? 'aspect-square' : 'aspect-video'} h-full w-full object-cover`}
        />
      ))}
    </div>
  )
}

function PrimaryMedia({ media, ratio = 'square' }: { media: MediaItem[]; ratio?: 'square' | 'video' | 'vertical' }) {
  const aspect = ratio === 'video' ? 'aspect-video' : ratio === 'vertical' ? 'aspect-[9/14]' : 'aspect-square'
  const first = media[0]
  if (!first) return null
  return <MediaItemView item={first} className={`${aspect} h-full w-full object-cover`} />
}

function Action({ icon, label }: { icon: React.ReactNode; label?: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-ink-500">
      {icon}
      {label}
    </span>
  )
}

function PreviewAvatar({
  platform,
  src,
  name,
  background,
  size,
}: {
  platform: PlatformId
  src?: string
  name: string
  background: string
  size: number
}) {
  return (
    <span className="relative shrink-0" style={{ width: size, height: size }}>
      <ProfileImage src={src} name={name} background={background} size={size} />
      <span className="absolute -bottom-0.5 -right-0.5">
        <PlatformBadge platform={platform} size={Math.max(7, Math.round(size * 0.22))} />
      </span>
    </span>
  )
}

export function PostPreview({
  platform,
  channel,
  content,
  media,
}: {
  platform: PlatformId
  channel: Channel
  content: string
  media: MediaItem[]
}) {
  const network = PLATFORMS[platform]
  const name = channel.name
  const handle = channel.handle
  const avatar = channel.avatarColor || network.gradient
  const avatarUrl = channelProfileSource(channel)
  const text = content.trim()
  const hasPostBody = Boolean(text || media.length)

  if (platform === 'instagram') {
    return (
      <article className="overflow-hidden rounded-[12px] border border-line bg-surface shadow-card">
        <header className="flex items-center gap-2.5 px-3 py-2">
          <PreviewAvatar platform={platform} src={avatarUrl} name={name} background={avatar} size={32} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-[12px] font-semibold text-ink-900">
              {(handle || name).replace(/^@/, '')}
            </p>
          </div>
          <MoreHorizontal size={17} />
        </header>
        <PrimaryMedia media={media} />
        {hasPostBody && (
          <div className="p-2.5">
            <div className="flex items-center gap-3 text-ink-900">
              <Heart size={18} strokeWidth={1.8} />
              <MessageCircle size={18} strokeWidth={1.8} />
              <Send size={17} strokeWidth={1.8} />
              <Bookmark size={17} strokeWidth={1.8} className="ml-auto" />
            </div>
            {text && (
              <p className="mt-2 whitespace-pre-wrap text-[12px] leading-relaxed text-ink-900">
                {handle && <span className="mr-1 font-semibold">{handle.replace(/^@/, '')}</span>}
                {text}
              </p>
            )}
          </div>
        )}
      </article>
    )
  }

  if (platform === 'threads') {
    return (
      <article className="rounded-[12px] border border-line bg-surface p-3 shadow-card">
        <div className="flex gap-2.5">
          <PreviewAvatar platform={platform} src={avatarUrl} name={name} background={avatar} size={36} />
          <div className="min-w-0 flex-1">
            <header className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13px] font-semibold text-ink-900">{name}</p>
                <p className="text-[11px] text-ink-500">{handle}</p>
              </div>
              <MoreHorizontal size={17} className="text-ink-500" />
            </header>
            {text && <p className="mt-1.5 whitespace-pre-wrap text-[13px] leading-[1.45] text-ink-900">{text}</p>}
            <MediaGrid media={media} />
            {hasPostBody && (
              <div className="mt-2.5 flex items-center gap-4 text-ink-700">
                <Heart size={17} strokeWidth={1.7} />
                <MessageCircle size={17} strokeWidth={1.7} />
                <Repeat2 size={17} strokeWidth={1.7} />
                <Send size={17} strokeWidth={1.7} />
              </div>
            )}
          </div>
        </div>
      </article>
    )
  }

  if (platform === 'x') {
    return (
      <article className="rounded-[12px] border border-line bg-surface p-3 shadow-card">
        <div className="flex gap-2.5">
          <PreviewAvatar platform={platform} src={avatarUrl} name={name} background={avatar} size={36} />
          <div className="min-w-0 flex-1">
            <header className="flex min-w-0 items-center gap-1 text-[12px]">
              <span className="truncate font-semibold text-ink-900">{name}</span>
              {handle && <span className="truncate text-ink-500">{handle}</span>}
              <MoreHorizontal size={17} className="ml-auto shrink-0 text-ink-400" />
            </header>
            {text && <p className="mt-1 whitespace-pre-wrap text-[13px] leading-relaxed text-ink-900">{text}</p>}
            <MediaGrid media={media} />
            {hasPostBody && (
              <div className="mt-3 flex max-w-sm items-center justify-between text-ink-500">
                <MessageCircle size={16} />
                <Repeat2 size={16} />
                <Heart size={16} />
                <BarChart3 size={16} />
                <Bookmark size={16} />
                <Share2 size={16} />
              </div>
            )}
          </div>
        </div>
      </article>
    )
  }

  if (platform === 'linkedin') {
    return (
      <article className="overflow-hidden rounded-[12px] border border-line bg-surface shadow-card">
        <header className="flex items-start gap-2.5 p-3">
          <PreviewAvatar platform={platform} src={avatarUrl} name={name} background={avatar} size={36} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-[13px] font-semibold text-ink-900">{name}</p>
            {handle && <p className="text-[10.5px] text-ink-500">{handle}</p>}
          </div>
          <MoreHorizontal size={18} className="text-ink-500" />
        </header>
        {text && <p className="whitespace-pre-wrap px-3 pb-2.5 text-[12.5px] leading-relaxed text-ink-900">{text}</p>}
        {media.length > 0 && <PrimaryMedia media={media} ratio="video" />}
        {hasPostBody && (
          <div className="mx-3.5 flex items-center justify-between border-t border-line py-2.5">
            <Action icon={<ThumbsUp size={16} />} label="Like" />
            <Action icon={<MessageCircle size={16} />} label="Comment" />
            <Action icon={<Repeat2 size={16} />} label="Repost" />
            <Action icon={<Send size={16} />} label="Send" />
          </div>
        )}
      </article>
    )
  }

  if (platform === 'facebook') {
    return (
      <article className="overflow-hidden rounded-[12px] border border-line bg-surface shadow-card">
        <header className="flex items-center gap-2.5 p-3">
          <PreviewAvatar platform={platform} src={avatarUrl} name={name} background={avatar} size={36} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-[13px] font-semibold text-ink-900">{name}</p>
            {handle && <p className="text-[10.5px] text-ink-500">{handle}</p>}
          </div>
          <MoreHorizontal size={18} className="text-ink-500" />
        </header>
        {text && <p className="whitespace-pre-wrap px-3 pb-2.5 text-[13px] leading-relaxed text-ink-900">{text}</p>}
        {media.length > 0 && <PrimaryMedia media={media} ratio="video" />}
        {hasPostBody && (
          <div className="flex items-center justify-between border-t border-line px-5 py-2.5">
            <Action icon={<ThumbsUp size={16} />} label="Like" />
            <Action icon={<MessageCircle size={16} />} label="Comment" />
            <Action icon={<Share2 size={16} />} label="Share" />
          </div>
        )}
      </article>
    )
  }

  if (platform === 'youtube') {
    return (
      <article className="overflow-hidden rounded-xl border border-line bg-surface shadow-card">
        {media.length > 0 && (
          <div className="relative bg-black">
            <PrimaryMedia media={media} ratio="video" />
            <span className="absolute left-1/2 top-1/2 grid size-10 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-black/70 text-white">
              <Play size={18} fill="currentColor" />
            </span>
          </div>
        )}
        <div className="p-3.5">
          {text && <p className="text-[13px] font-semibold leading-snug text-ink-900">{text}</p>}
          <div className="mt-3 flex items-center gap-2.5">
            <PreviewAvatar platform={platform} src={avatarUrl} name={name} background={avatar} size={30} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-[11.5px] font-medium text-ink-900">{name}</p>
              {handle && <p className="text-[10px] text-ink-500">{handle}</p>}
            </div>
            <button className="rounded-full bg-ink-900 px-3 py-1.5 text-[10px] font-semibold text-white">Subscribe</button>
          </div>
        </div>
      </article>
    )
  }

  if (platform === 'tiktok') {
    return (
      <article className="mx-auto max-w-[280px] overflow-hidden rounded-2xl bg-[#111] text-white shadow-card">
        <div className="relative">
          <PrimaryMedia media={media} ratio="vertical" />
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent p-4 pt-16">
            <p className="text-[12px] font-semibold">{handle}</p>
            {text && <p className="mt-1 whitespace-pre-wrap text-[11.5px] leading-relaxed text-white">{text}</p>}
          </div>
          <div className="absolute bottom-5 right-3 flex flex-col items-center gap-4">
            <PreviewAvatar platform={platform} src={avatarUrl} name={name} background={avatar} size={32} />
            <Heart size={22} fill="white" />
            <MessageCircle size={22} fill="white" />
            <Share2 size={22} fill="white" />
          </div>
        </div>
      </article>
    )
  }

  if (platform === 'bluesky') {
    return (
      <article className="rounded-[12px] border border-line bg-surface p-3 shadow-card">
        <div className="flex gap-2.5">
          <PreviewAvatar platform={platform} src={avatarUrl} name={name} background={avatar} size={36} />
          <div className="min-w-0 flex-1">
            <header className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13px] font-semibold text-ink-900">{name}</p>
                {handle && <p className="text-[11px] text-ink-500">{handle}</p>}
              </div>
              <MoreHorizontal size={17} className="text-ink-400" />
            </header>
            {text && <p className="mt-2 whitespace-pre-wrap text-[13px] leading-relaxed text-ink-900">{text}</p>}
            <MediaGrid media={media} />
            {hasPostBody && (
              <div className="mt-3 flex max-w-[260px] justify-between text-ink-500">
                <MessageCircle size={16} />
                <Repeat2 size={16} />
                <Heart size={16} />
                <Share2 size={16} />
              </div>
            )}
          </div>
        </div>
      </article>
    )
  }

  return (
    <article className="rounded-[12px] border border-line bg-surface p-3 shadow-card">
      <div className="flex gap-2.5">
        <PreviewAvatar platform={platform} src={avatarUrl} name={name} background={avatar} size={36} />
        <div className="min-w-0 flex-1">
          <header className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] font-semibold text-ink-900">{name}</p>
              {handle && <p className="text-[11px] text-ink-500">{handle}</p>}
            </div>
            <PlatformIcon platform="mastodon" size={17} />
            <MoreHorizontal size={17} className="text-ink-400" />
          </header>
          {text && <p className="mt-2 whitespace-pre-wrap text-[13px] leading-relaxed text-ink-900">{text}</p>}
          <MediaGrid media={media} />
          {hasPostBody && (
            <div className="mt-3 flex max-w-[280px] justify-between text-ink-500">
              <MessageCircle size={16} />
              <Repeat2 size={16} />
              <Star size={16} />
              <Bookmark size={16} />
              <Share2 size={16} />
            </div>
          )}
        </div>
      </div>
    </article>
  )
}
